<script setup lang="ts">
const localePath = useLocalePath();
const { t, tm, rt } = useI18n() as any;

useHead({
  titleTemplate: () => t("twoFactorAuth.hero.title") + " | SMSBAT",
  meta: [
    { name: "description", content: () => t("twoFactorAuth.hero.description") },
    { name: "keywords", content: "Two-Factor Authentication, 2FA, SMS OTP, Security, SMSBAT" },
    { property: "og:title", content: () => t("twoFactorAuth.hero.title") },
    { property: "og:description", content: () => t("twoFactorAuth.hero.description") },
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: "SMSBAT" },
  ]
});
</script>

<template>
  <div>
    <!-- Hero Section -->
    <div class="main-banner" style="background: linear-gradient(135deg, rgba(48, 112, 182, 0.05) 0%, rgba(48, 112, 182, 0.15) 100%);">
      <div class="main-banner-content container mx-auto px-4 py-20 flex flex-col md:flex-row items-center">
        <div class="banner-text md:w-full max-w-3xl text-center mx-auto">
          <div class="banner-title text-4xl md:text-5xl font-extrabold mb-6 text-gray-900">
            {{ $t('twoFactorAuth.hero.title') }}
          </div>
          <div class="banner-description text-xl text-gray-700 mb-6 font-medium">
            {{ $t('twoFactorAuth.hero.subtitle') }}
          </div>
          <div class="banner-description text-gray-600 mb-10 text-lg">
            {{ $t('twoFactorAuth.hero.description') }}
          </div>
          <div class="banner-action mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://omni.smsbat.com/auth/login?regView=true" target="_blank" rel="noopener noreferrer" class="nav-link button filled inline-block px-10 py-4 rounded-xl bg-[#3070b6] text-white font-bold transition hover:bg-[#255a92] shadow-lg w-full sm:w-auto text-center">
              <p>{{ $t('links.registerBusinessAccount') }}</p>
            </a>
            <a href="#ContactForm" class="nav-link button outlined inline-block px-10 py-4 rounded-xl border-2 border-[#3070b6] text-[#3070b6] font-bold transition hover:bg-[#3070b6] hover:text-white shadow-lg w-full sm:w-auto text-center">
              <p>{{ $t('actions.contactUs') }}</p>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Features Section -->
    <div class="page-section py-20 bg-white">
      <div class="container mx-auto px-4">
        <div class="section-head text-center mb-16">
          <h2 class="section-head-title text-3xl md:text-4xl font-bold text-gray-900">{{ $t('twoFactorAuth.features.title') }}</h2>
        </div>
        <div class="section-content">
          <div class="features-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div class="feature-card p-8 bg-gray-50 rounded-2xl transition hover:-translate-y-2 hover:shadow-lg border border-gray-100 flex flex-col items-center text-center" v-for="item in Object.values(tm('twoFactorAuth.features.list'))" :key="rt(item.title)">
              <div class="feature-icon mb-6 w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br from-[#3070b6] to-[#5d87ff] text-white shadow-md">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1ZM12 7C13.4 7 14.8 8.6 14.8 10.5C14.8 12.4 13.4 14 12 14C10.6 14 9.2 12.4 9.2 10.5C9.2 8.6 10.6 7 12 7ZM18 18C16.5 16 14 15 12 15C10 15 7.5 16 6 18V19H18V18Z"></path>
                </svg>
              </div>
              <h3 class="feature-title text-xl font-bold text-gray-900 mb-4">{{ rt(item.title) }}</h3>
              <p class="feature-description text-gray-600 leading-relaxed">{{ rt(item.description) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <ContactForm />
  </div>
</template>

<style scoped lang="scss">
.main-banner {
  min-height: 50vh;
  display: flex;
  align-items: center;
}
</style>
